import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Box } from '@mui/material';
import { removeJWT } from 'src/store/slices/jwtTokenSlice';

import { PATHS } from 'src/constants/config';
import NavBar from 'src/components/NavBar/NavBar';
import MobileNavBar from 'src/components/NavBar/MobileNavBar';
import { useDecodedToken } from 'src/hooks/useDecodedToken';
import { useRouteAccess } from 'src/hooks/useRouteAccess';

const ProtectedLayout = ({ children, showNavBar = true, disabled = false }) => {
  const token = useSelector((state) => state.jwtToken.token);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { decodedToken, isExpert } = useDecodedToken();
  const { isPathAllowed } = useRouteAccess();

  useEffect(() => {
    if (disabled) return;

    if (!token || !decodedToken) {
      dispatch(removeJWT());
      navigate(PATHS.AUTH.LOGIN.value);
      return;
    }

    // Check user type access control
    if (!isPathAllowed(location.pathname)) {
      navigate(PATHS.AUTH.LOGIN.value);
      return;
    }
  }, [token, navigate, disabled, dispatch, location.pathname, decodedToken, isPathAllowed]);

  return (
    <Box sx={{ display: { xs: 'block', sm: 'flex' } }}>
      {showNavBar && !isExpert && !isMobile && <NavBar />}
      {showNavBar && !isExpert && isMobile && <MobileNavBar />}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          transition: (theme) =>
            theme.transitions.create(['margin', 'width'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          pb: showNavBar && isMobile ? '80px' : '0px',
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

ProtectedLayout.propTypes = {
  children: PropTypes.node.isRequired,
  showNavBar: PropTypes.bool,
  showFooter: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default ProtectedLayout;
